"use client";

import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LabelList,
  CartesianGrid,
} from "recharts";

import { ChartTooltipContent, ChartContainer } from "../ui/chart";
import { BoxContainerChart } from "../horizontalBarChart/styles";

type Item = { name: string; value: number };

type Props = {
  data: Item[];
  nameKey?: string;
  valueKey?: string;
  title?: string;
  color?: string;
  height?: number;
  barSize?: number;
  barGap?: number | string;
  barCategoryGap?: number | string;
  xAxisAngle?: number;
  yTickFormatter?: (v: any) => string;
  valueFormatter?: (v: any) => string;
};

const VerticalBarChart: React.FC<Props> = ({
  data,
  nameKey = "name",
  valueKey = "value",
  title,
  color = "#0f4c81",
  height = 350, // aumento default
  barSize = 20,
  barGap,
  barCategoryGap,
  xAxisAngle = -30,
  yTickFormatter,
  valueFormatter,
}) => {
  const formattedData = data || [];

  // compute gaps dynamically when not provided to improve spacing for few bars
  const count = formattedData.length;
  const computedBarCategoryGap =
    typeof barCategoryGap !== "undefined"
      ? barCategoryGap
      : count <= 3
        ? "70%"
        : count === 4
          ? "55%"
          : count === 5
            ? "45%"
            : "25%";

  const computedBarGap =
    typeof barGap === "number"
      ? barGap
      : count <= 3
        ? 20
        : count === 4
          ? 12
          : count === 5
            ? 8
            : 4;

  // compute bar size when not provided: smaller when chart is short or many items
  const computedBarSize =
    typeof barSize === "number"
      ? barSize
      : Math.max(
          12,
          Math.min(64, Math.floor((height || 300) / Math.max(2, count + 1))),
        );

  // default y tick formatter to show percent when valueFormatter appends %
  const effectiveYTickFormatter =
    yTickFormatter ||
    ((v: any) => (valueFormatter ? valueFormatter(v) : String(v)));

  return (
    <BoxContainerChart
      config={{ y: { color } }}
      className="w-full items-center"
    >
      <div className="w-full flex items-end" style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={formattedData}
            height={height}
            margin={{ top: 40, right: 20, left: 20, bottom: 30 }}
            barGap={computedBarGap}
            barCategoryGap={computedBarCategoryGap}
          >
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis
              dataKey={nameKey}
              tickLine={false}
              axisLine={false}
              interval={0}
              angle={xAxisAngle}
              textAnchor="end"
              height={60}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickFormatter={effectiveYTickFormatter}
            />
            <Tooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  hideLabel
                  nameKey={nameKey}
                  formatter={(val: any) =>
                    valueFormatter ? valueFormatter(val) : val
                  }
                />
              }
            />
            <Bar
              dataKey={valueKey}
              name={title}
              fill={color}
              radius={[6, 6, 0, 0]}
              animationDuration={900}
              animationEasing="ease-out"
              barSize={computedBarSize}
            >
              <LabelList
                dataKey={valueKey}
                position="top"
                formatter={(v: any) =>
                  valueFormatter
                    ? valueFormatter(v)
                    : v !== undefined
                      ? v.toLocaleString()
                      : v
                }
                style={{ fontSize: 12 }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </BoxContainerChart>
  );
};

export default VerticalBarChart;
